import { useQueryClient } from '@tanstack/react-query';
import useMutationApi from '../useMutationApi';
import { orpc } from '@/api/rpc/client';

interface ResetForecastPayload {
  agreementIds?: string[];
}

interface ResetForecastResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    message: string;
    updatedCount: number;
    skippedCount: number;
    deletedReportedCount?: number;
  };
}

/**
 * Reset forecast for one or more agreements (admin only)
 * Sets lastForecastGeneratedAt to null, triggering regeneration on next fetch
 * Automatically filters out historical agreements (isHistorical = true)
 */
export function useResetForecast() {
  const queryClient = useQueryClient();

  return useMutationApi<ResetForecastPayload, ResetForecastResponse>({
    endpoint: 'rebates/agreements/reset-forecast',
    method: 'POST',
    options: {
      onSuccess: () => {
        // Invalidate all oRPC forecast queries (both fetch and generateForAgreement)
        queryClient.invalidateQueries({
          queryKey: orpc.rebates.forecast.key(),
        });
      },
    },
    successMessage: 'Forecast reset successfully',
  });
}

/**
 * Reset forecast AND delete reported breakdowns for one or more agreements (admin only)
 * Deletes all REPORTED type breakdowns, then sets lastForecastGeneratedAt to null
 * Automatically filters out historical agreements (isHistorical = true)
 */
export function useResetForecastAndReported() {
  const queryClient = useQueryClient();

  return useMutationApi<ResetForecastPayload, ResetForecastResponse>({
    endpoint: 'rebates/agreements/reset-forecast-and-reported',
    method: 'POST',
    options: {
      onSuccess: () => {
        // Invalidate all oRPC forecast queries (both fetch and generateForAgreement)
        queryClient.invalidateQueries({
          queryKey: orpc.rebates.forecast.key(),
        });
      },
    },
    successMessage: 'Forecast and reported data reset successfully',
  });
}
